import { useEffect, useRef } from 'react';

import {
  generatePath, useNavigate, useParams, useSearchParams
} from 'react-router-dom';

import { gql, useMutation } from '@zenbusiness/application-commons-graphql';
import Button from '@zenbusiness/zui/Button';
import Container from '@zenbusiness/zui/Container';
import PageLoading from '@zenbusiness/zui/PageLoading';
import Stack from '@zenbusiness/zui/Stack';
import Text from '@zenbusiness/zui/Text';

import {
  invoicePayment, invoicePaymentCheckout, invoicePaymentReturn
} from '#client/constants/routes';
import { KlarnaPaymentWidget } from '#client/directEntry/MFEs';

const PAY_BILLING_INVOICE = gql`
  mutation PayBillingInvoice($input: BillingPayInvoiceInput!) {
    payBillingInvoice(input: $input) {
      outcome
      paymentError {
        error
        declineCode
        code
        message
        transactionId
      }
      errorDetails
    }
  }
`;

// Builds the return URL for all navigate-to-PaymentReturn calls.
// Centralises param construction so URL shape changes only need to happen here.
function buildReturnUrl(status, {
  amountInCents, accountUuid, companyName, invoiceId, unpaidCount, errorMessage
} = {}) {
  const params = new URLSearchParams({ status, amountInCents: String(amountInCents) });
  if (companyName) params.set('companyName', companyName);
  if (invoiceId) params.set('invoiceId', invoiceId);
  params.set('unpaidCount', unpaidCount);
  if (errorMessage) params.set('errorMessage', errorMessage);
  return `${generatePath(invoicePaymentReturn, { accountUuid })}?${params.toString()}`;
}

export default function KlarnaPaymentCheckout() {
  const { accountUuid } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  // finalizedRef guards against React StrictMode's double-invocation in dev, which would fire
  // the mutation twice on the same render cycle.
  const finalizedRef = useRef(false);

  // Legacy path: three_d_secure_action_result_token_id in the URL triggers the useEffect below to
  // call payBillingInvoice directly with the result token. This is NOT the normal Klarna flow —
  // the normal post-redirect path goes through the offscreen KlarnaPaymentWidget (savedActionTokenId)
  // with alreadyConfirmed=true. This handles the rare case where the result token was appended to
  // the URL instead of going through the widget callback.
  const threeDSecureResultTokenId = searchParams.get('three_d_secure_action_result_token_id') || '';
  const invoiceId = searchParams.get('invoiceId') || '';
  // Parse amountInCents with an explicit missing-vs-zero distinction:
  // Number(...) || 0 would collapse a legitimate $0 invoice to the "no data" fallback.
  const rawAmountParam = searchParams.get('amountInCents');
  const amountInCents = rawAmountParam !== null && Number.isFinite(Number(rawAmountParam))
    ? Number(rawAmountParam)
    : 0;
  const hasAmountParam = rawAmountParam !== null;
  const companyName = searchParams.get('companyName') || '';
  const unpaidCount = searchParams.get('unpaidCount') || '0';

  // When Stripe redirects back from Klarna it appends redirect_status to indicate outcome.
  // We use it to determine whether to restore the ThreeDSecureChallenge (succeeded) or
  // navigate to the failure page (failed/canceled).
  const stripeRedirectStatus = searchParams.get('redirect_status');
  const savedActionTokenId = stripeRedirectStatus === 'succeeded' && !threeDSecureResultTokenId
    ? localStorage.getItem(`klarna_pending_action_token_${invoiceId}`)
    : null;

  // InvoicePayments writes line items to sessionStorage at payment initiation so they survive
  // the navigation to this page. Parse defensively — a missing or malformed entry is fine.
  let lineItems = [];
  try {
    lineItems = JSON.parse(sessionStorage.getItem(`invoice_lineitems_${invoiceId}`) || '[]');
  } catch { /* ignore */ }

  // Stripe appends payment_intent and payment_intent_client_secret to the returnUrl on redirect.
  // Strip them from the URL — they end up in server access logs and browser history otherwise.
  // Exception: when the offscreen widget path is active (savedActionTokenId set), payment_intent
  // must remain for two reasons: (1) Recurly's ThreeDSecureAction reads redirect_status +
  // payment_intent from the URL to detect the Klarna redirect and fire onToken, and (2)
  // handlePayInvoice reads payment_intent as paymentIntentId for the alreadyConfirmed server call.
  // Navigation away after the 3DS flow completes cleans the params naturally.
  useEffect(() => {
    if (savedActionTokenId) return;
    const url = new URL(window.location.href);
    if (url.searchParams.has('payment_intent') || url.searchParams.has('payment_intent_client_secret')) {
      url.searchParams.delete('payment_intent');
      url.searchParams.delete('payment_intent_client_secret');
      window.history.replaceState(null, '', url.toString());
    }
  }, [savedActionTokenId]);

  // User exited or cancelled the Klarna flow — redirect_status is 'failed' or 'canceled'.
  // Clean up localStorage and send them to the failure return page.
  useEffect(() => {
    if (!stripeRedirectStatus || stripeRedirectStatus === 'succeeded') return;
    localStorage.removeItem(`klarna_pending_action_token_${invoiceId}`);
    localStorage.removeItem(`klarna_pending_ctoken_${invoiceId}`);
    navigate(
      buildReturnUrl('failure', {
        amountInCents,
        accountUuid,
        companyName,
        invoiceId,
        unpaidCount,
        errorMessage: 'Klarna payment was not completed. Please try again.'
      }),
      { replace: true }
    );
  }, [
    stripeRedirectStatus, invoiceId, amountInCents, accountUuid, companyName, unpaidCount, navigate
  ]);

  const [payBillingInvoice] = useMutation(PAY_BILLING_INVOICE, { noThrow: true });

  useEffect(() => {
    if (!threeDSecureResultTokenId || finalizedRef.current) return;
    finalizedRef.current = true;

    // Read the confirmation token stored by KlarnaPaymentWidget before the Klarna redirect.
    // The widget stores it in localStorage keyed to the invoice so it survives the redirect.
    const confirmationTokenId = localStorage.getItem(`klarna_pending_ctoken_${invoiceId}`);

    if (!confirmationTokenId || !invoiceId) {
      navigate(
        buildReturnUrl('failure', {
          amountInCents,
          accountUuid,
          companyName,
          invoiceId,
          unpaidCount,
          errorMessage: 'Payment session expired or invalid. Please try again.'
        }),
        { replace: true }
      );
      return;
    }

    payBillingInvoice({
      variables: {
        input: {
          confirmationTokenId,
          invoiceId,
          accountUuid,
          amountInCents,
          returnUrl: '', // required by base schema; not used server-side
          threeDSecureResultTokenId
        }
      }
    }).then(({ data }) => {
      const result = data?.payBillingInvoice;
      localStorage.removeItem(`klarna_pending_ctoken_${invoiceId}`);
      if (result?.outcome === 'succeeded') {
        navigate(buildReturnUrl('success', {
          amountInCents, accountUuid, companyName, invoiceId, unpaidCount
        }), { replace: true });
      } else {
        const errorMessage = result?.paymentError?.message || 'Payment could not be finalized. Please try again.';
        navigate(buildReturnUrl('failure', {
          amountInCents, accountUuid, companyName, invoiceId, unpaidCount, errorMessage
        }), { replace: true });
      }
    }).catch(() => {
      navigate(
        buildReturnUrl('failure', {
          amountInCents,
          accountUuid,
          companyName,
          invoiceId,
          unpaidCount,
          errorMessage: 'An unexpected error occurred. Please try again.'
        }),
        { replace: true }
      );
    });
  }, [
    threeDSecureResultTokenId, invoiceId, accountUuid,
    companyName, amountInCents, unpaidCount, navigate, payBillingInvoice
  ]);

  // Build returnUrl with all context params so that when Stripe redirects back from Klarna,
  // KlarnaPaymentCheckout has invoiceId/amountInCents available to mount the widget
  // offscreen and complete call 2 via ThreeDSecureChallenge.
  const returnUrlParams = new URLSearchParams();
  if (invoiceId) returnUrlParams.set('invoiceId', invoiceId);
  if (hasAmountParam) returnUrlParams.set('amountInCents', String(amountInCents));
  if (companyName) returnUrlParams.set('companyName', companyName);
  returnUrlParams.set('unpaidCount', unpaidCount);
  const returnUrlQs = returnUrlParams.toString();
  const returnUrlPath = generatePath(invoicePaymentCheckout, { accountUuid });
  const returnUrl = `${window.location.origin}${returnUrlPath}${returnUrlQs ? `?${returnUrlQs}` : ''}`;

  const stripePaymentIntentId = searchParams.get('payment_intent');

  // confirmationTokenId is forwarded to the mutation on both paths (required by the schema) but
  // ignored server-side on the alreadyConfirmed path — the server uses paymentIntentId to verify
  // the PI directly and marks the invoice without touching Stripe again.
  const handlePayInvoice = async ({
    confirmationTokenId, threeDSecureResultTokenId: resultTokenId, alreadyConfirmed
  }) => {
    // alreadyConfirmed flags the offscreen widget path (post-Klarna redirect).
    // KlarnaPaymentCheckout owns navigation for this path since the widget is hidden and
    // KlarnaPaymentCheckout has all the context (amountInCents, companyName, etc.) to build the return URL.
    const isOffscreenCall = Boolean(alreadyConfirmed);
    try {
      const { data } = await payBillingInvoice({
        variables: {
          input: {
            confirmationTokenId,
            accountUuid,
            invoiceId,
            amountInCents,
            returnUrl,
            ...(resultTokenId && { threeDSecureResultTokenId: resultTokenId }),
            ...(alreadyConfirmed && {
              alreadyConfirmed: true,
              paymentIntentId: stripePaymentIntentId
            })
          }
        }
      });
      const result = data?.payBillingInvoice;

      if (isOffscreenCall) {
        localStorage.removeItem(`klarna_pending_action_token_${invoiceId}`);
        localStorage.removeItem(`klarna_pending_ctoken_${invoiceId}`);
        if (result?.outcome === 'succeeded') {
          navigate(buildReturnUrl('success', {
            amountInCents, accountUuid, companyName, invoiceId, unpaidCount
          }), { replace: true });
        } else {
          const errorMessage = result?.paymentError?.message || 'Payment could not be finalized. Please try again.';
          navigate(buildReturnUrl('failure', {
            amountInCents, accountUuid, companyName, invoiceId, unpaidCount, errorMessage
          }), { replace: true });
        }
      }

      return result;
    } catch (e) {
      if (isOffscreenCall) {
        localStorage.removeItem(`klarna_pending_action_token_${invoiceId}`);
        localStorage.removeItem(`klarna_pending_ctoken_${invoiceId}`);
        navigate(buildReturnUrl('failure', {
          amountInCents,
          accountUuid,
          companyName,
          invoiceId,
          unpaidCount,
          errorMessage: 'An unexpected error occurred. Please try again.'
        }), { replace: true });
        return undefined;
      }
      throw e;
    }
  };

  // three_d_secure_action_result_token_id in the URL triggers the useEffect above to fire
  // payBillingInvoice directly (legacy path). Show a spinner while that round-trip is in flight.
  if (threeDSecureResultTokenId) return <PageLoading />;

  // User exited or cancelled the Klarna flow — show a spinner while the useEffect navigates
  // to the failure return page. Prevents the payment form from flashing before navigation.
  if (stripeRedirectStatus && stripeRedirectStatus !== 'succeeded') return <PageLoading />;

  // After a successful Klarna redirect, Recurly processes the Stripe params offscreen and
  // fires the second payBillingInvoice call. Show a spinner while that round-trip is in flight.

  if (savedActionTokenId) {
    return (
      <>
        <PageLoading />
        <div style={{ position: 'fixed', left: '-10000px', top: 0 }}>
          <KlarnaPaymentWidget
            accountUuid={accountUuid}
            amountInCents={amountInCents}
            companyName={companyName}
            initialActionTokenId={savedActionTokenId}
            invoiceNumber={invoiceId}
            lineItems={lineItems}
            unpaidCount={Number(unpaidCount)}
            onPayInvoice={handlePayInvoice}
          />
        </div>
      </>
    );
  }

  // Guard against landing on this page with no invoice data (e.g., direct URL navigation).
  // hasAmountParam distinguishes "param missing" from "param is 0" (a valid zero-amount invoice).
  // Show a visible fallback rather than a blank screen so users can return to their invoice list.
  if (!hasAmountParam) {
    const backParams = new URLSearchParams();
    if (companyName) backParams.set('companyName', companyName);
    const query = backParams.toString();
    const backUrl = `${generatePath(invoicePayment, { accountUuid })}${query ? `?${query}` : ''}`;
    return (
      <Container maxWidth="sm" sx={{ paddingTop: 6, paddingBottom: 6 }}>
        <Stack gap={3}>
          <Text>No invoice data found.</Text>
          <Button
            variant="contained"
            sx={{ alignSelf: 'flex-start' }}
            onClick={() => navigate(backUrl)}
          >
            Back to invoices
          </Button>
        </Stack>
      </Container>
    );
  }

  return (
    <Container maxWidth="sm" sx={{ paddingTop: 6, paddingBottom: 6 }}>
      <KlarnaPaymentWidget
        accountUuid={accountUuid}
        amountInCents={amountInCents}
        companyName={companyName}
        invoiceNumber={invoiceId}
        lineItems={lineItems}
        unpaidCount={Number(unpaidCount)}
        onPayInvoice={handlePayInvoice}
      />
    </Container>
  );
}
